import React, { useState, useRef, Fragment } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, Transition } from "@headlessui/react";
import { Button, TextField, Select, MenuItem, FormControl, InputLabel, Alert, Table, TableBody, TableCell, TableHead, TableRow, Checkbox } from "@mui/material";
import { DocumentArrowUpIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { useDropzone } from "react-dropzone";
import toast from "react-hot-toast";
import { fetchProducts, createProduct, bulkCreateProducts } from "../../../../api/product";
import { bulkImportCountItems } from "../../../../api/inventory/count_items";
import { isValidInventoryQuantity, normalizeInventoryQuantity } from "../../../../pages/dashboard/inventory/inventory-quantity";

/**
 * CSV/Excel Import Dialog with intelligent column mapping
 */
export const CountCSVImportDialog = ({
  open,
  onClose,
  onImportComplete,
  countId,
}) => {
  const { t } = useTranslation();
  const [file, setFile] = useState(null);
  const [csvData, setCsvData] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [columnMapping, setColumnMapping] = useState({});
  const [previewData, setPreviewData] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [errors, setErrors] = useState([]);
  const [missingProducts, setMissingProducts] = useState([]);
  const [pendingItems, setPendingItems] = useState([]);
  const [productChoices, setProductChoices] = useState({}); // Track user choices for missing products
  const [importProgress, setImportProgress] = useState({ current: 0, total: 0 });
  const fileInputRef = useRef(null);

  // Field mappings for intelligent detection
  const fieldMappings = {
    product_id: ["product_id", "product id", "produkt id", "produktid", "id"],
    item_number: ["item_number", "item number", "artikelnr", "artikelnr.", "artnr", "artnr.", "product number", "produktnummer"],
    title: ["title", "tittel", "name", "navn", "product name", "produktnavn", "description", "beskrivelse"],
    quantity: ["quantity", "mengde", "qty", "antall", "amount"],
    purchase_price: ["purchase_price", "purchase price", "innkjopspris", "innkjøpspris", "cost", "kostnad"],
    note: ["note", "notat", "comment", "kommentar", "remarks", "merknad"],
  };

  const detectColumnMapping = (csvHeaders) => {
    const mapping = {};
    const lowerHeaders = csvHeaders.map((h) => h.toLowerCase().trim());

    Object.keys(fieldMappings).forEach((field) => {
      const possibleNames = fieldMappings[field];
      const foundIndex = lowerHeaders.findIndex((header) =>
        possibleNames.some((name) => header.includes(name))
      );
      if (foundIndex !== -1) {
        mapping[field] = csvHeaders[foundIndex];
      }
    });

    return mapping;
  };

  // Remove BOM and normalize text encoding
  const normalizeText = (text) => {
    if (!text || typeof text !== 'string') return text;
    
    // Remove UTF-8 BOM if present
    if (text.charCodeAt(0) === 0xFEFF) {
      text = text.slice(1);
    }
    // Remove other BOM variants
    text = text.replace(/^\uFEFF/, "");
    
    // Fix common double-encoding issues (when UTF-8 is interpreted as Latin-1)
    // These patterns occur when UTF-8 bytes are interpreted as Latin-1 characters
    text = text.replace(/\u00C3\u00A6/g, "æ"); // Fix double-encoded æ
    text = text.replace(/\u00C3\u00B8/g, "ø"); // Fix double-encoded ø
    text = text.replace(/\u00C3\u00A5/g, "å"); // Fix double-encoded å
    text = text.replace(/\u00C3\u0086/g, "Æ"); // Fix double-encoded Æ
    text = text.replace(/\u00C3\u0098/g, "Ø"); // Fix double-encoded Ø
    text = text.replace(/\u00C3\u0085/g, "Å"); // Fix double-encoded Å
    
    // Fix other common encoding issues
    text = text.replace(/\u00E6/g, "æ"); // Direct æ encoding issue
    text = text.replace(/\u00F8/g, "ø"); // Direct ø encoding issue
    text = text.replace(/\u00E5/g, "å"); // Direct å encoding issue
    
    return text;
  };

  const parseCSV = (text) => {
    // Normalize text encoding
    text = normalizeText(text);
    const lines = text.split("\n").filter((line) => line.trim());
    if (lines.length === 0) return { headers: [], data: [] };

    // Try to detect delimiter
    const delimiters = [",", ";", "\t"];
    let delimiter = ",";
    let maxFields = 0;

    for (const delim of delimiters) {
      const fieldCount = lines[0].split(delim).length;
      if (fieldCount > maxFields) {
        maxFields = fieldCount;
        delimiter = delim;
      }
    }

    // Parse headers
    const headerLine = lines[0];
    const headers = headerLine.split(delimiter).map((h) => h.trim().replace(/^["']|["']$/g, ""));

    // Parse data rows
    const data = [];
    for (let i = 1; i < Math.min(lines.length, 11); i++) {
      // Preview first 10 rows
      const values = lines[i].split(delimiter).map((v) => v.trim().replace(/^["']|["']$/g, ""));
      if (values.length === headers.length) {
        const row = {};
        headers.forEach((header, index) => {
          row[header] = values[index];
        });
        data.push(row);
      }
    }

    return { headers, data };
  };

  const handleFileSelect = async (acceptedFiles) => {
    if (acceptedFiles.length === 0) return;

    const selectedFile = acceptedFiles[0];
    setFile(selectedFile);
    setErrors([]);

    try {
      const reader = new FileReader();
      reader.onload = (e) => {
        let text = e.target.result;
        // Try to decode as UTF-8 first, fallback to other encodings if needed
        try {
          // Normalize text encoding
          text = normalizeText(text);
        } catch (err) {
          console.warn("Encoding normalization failed, using raw text");
        }
        
        const { headers, data } = parseCSV(text);
        
        if (headers.length === 0) {
          setErrors([t("inventory.count.csv.no_headers")]);
          return;
        }

        setHeaders(headers);
        setPreviewData(data);
        
        // Auto-detect column mapping
        const autoMapping = detectColumnMapping(headers);
        setColumnMapping(autoMapping);
      };
      reader.onerror = () => {
        setErrors([t("inventory.count.csv.read_error")]);
      };
      // Try UTF-8 first, which handles most cases including Norwegian characters
      reader.readAsText(selectedFile, "UTF-8");
    } catch (error) {
      console.error("Error reading file:", error);
      setErrors([t("inventory.count.csv.read_error")]);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleFileSelect,
    accept: {
      "text/csv": [".csv"],
      "application/vnd.ms-excel": [".xls"],
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
    },
    multiple: false,
  });

  const performImport = async (itemsToImport, importErrors = []) => {
    setIsProcessing(true);
    setImportProgress({ current: 0, total: itemsToImport.length });
    
    try {
      // Show progress: processing items
      setImportProgress({ current: itemsToImport.length, total: itemsToImport.length });
      
      const response = await bulkImportCountItems(countId, itemsToImport);
      
      // Check if API returned missing products (partial success)
      if (response?.missing_products && response.missing_products.length > 0) {
        // Store items and show validation dialog
        setPendingItems(itemsToImport);
        setMissingProducts(response.missing_products);
        setProductChoices({}); // Reset choices
        setIsProcessing(false);
        setImportProgress({ current: 0, total: 0 });
        
        // Show success message for imported items if any
        if (response.data && response.data.length > 0) {
          toast.success(t("inventory.count.csv.partial_import_success", { 
            imported: response.data.length,
            missing: response.missing_products.length 
          }));
        }
        return;
      }
      
      if (response?.success) {
        toast.success(t("inventory.count.csv.import_success", { count: itemsToImport.length }));
        // Always close dialogs and reset on successful import
        resetDialog();
        onClose();
        if (onImportComplete) {
          onImportComplete(response.data || []);
        }
        // Show errors as toast if any, but still close dialog
        if (importErrors.length > 0) {
          importErrors.forEach(error => toast.error(error));
        }
      } else {
        setErrors([response?.error || t("inventory.count.csv.import_error")]);
        setIsProcessing(false);
        setImportProgress({ current: 0, total: 0 });
      }
    } catch (error) {
      console.error("Import error:", error);
      setErrors([error.response?.data?.error || t("inventory.count.csv.import_error")]);
      setIsProcessing(false);
      setImportProgress({ current: 0, total: 0 });
    }
  };

  const handleImport = async () => {
    if (!file || !columnMapping.product_id && !columnMapping.item_number) {
      toast.error(t("inventory.count.csv.mapping_required"));
      return;
    }

    setIsProcessing(true);
    setErrors([]);
    setImportProgress({ current: 0, total: 0 });

    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        let text = e.target.result;
        // Normalize text encoding
        text = normalizeText(text);
        const lines = text.split("\n").filter((line) => line.trim());
        const delimiter = lines[0].includes(";") ? ";" : lines[0].includes("\t") ? "\t" : ",";
        
        const headerLine = lines[0];
        const csvHeaders = headerLine.split(delimiter).map((h) => h.trim().replace(/^["']|["']$/g, ""));

        const importErrors = [];

        // Parse all CSV rows and prepare for product lookup
        const itemsToLookup = [];
        for (let i = 1; i < lines.length; i++) {
          const line = lines[i].trim();
          // Skip empty lines
          if (!line) continue;
          
          const values = line.split(delimiter).map((v) => v.trim().replace(/^["']|["']$/g, ""));
          // Skip if row doesn't have enough columns or is empty
          if (values.length !== csvHeaders.length || values.every(v => !v)) continue;

          const row = {};
          csvHeaders.forEach((header, index) => {
            row[header] = values[index] || "";
          });

          // Map columns to fields
          const rawQuantity = columnMapping.quantity ? row[columnMapping.quantity] : "1";
          const quantity = normalizeInventoryQuantity(rawQuantity === "" ? "1" : rawQuantity);
          if (!isValidInventoryQuantity(quantity, { allowZero: true })) {
            importErrors.push(t("inventory.count.csv.invalid_quantity", { row: i + 1 }));
            continue;
          }
          
          // Skip if no product identifier
          if (!columnMapping.product_id && !columnMapping.item_number) {
            continue;
          }
          
          const item = {
            product_id: columnMapping.product_id ? normalizeText(row[columnMapping.product_id]) : null,
            item_number: columnMapping.item_number ? normalizeText(row[columnMapping.item_number]) : null,
            title: columnMapping.title ? normalizeText(row[columnMapping.title]) : null,
            quantity: quantity,
            purchase_price: columnMapping.purchase_price ? parseFloat(row[columnMapping.purchase_price]) || null : null,
            note: columnMapping.note ? normalizeText(row[columnMapping.note]) : "",
            row_number: i, // Track row number for error reporting
          };
          
          // Skip if no product identifier after normalization
          if (!item.product_id && !item.item_number) {
            continue;
          }

          itemsToLookup.push(item);
        }

        if (importErrors.length > 0) {
          setErrors(importErrors);
          setIsProcessing(false);
          setImportProgress({ current: 0, total: 0 });
          return;
        }

        // Fetch product data for items that don't have purchase_price or need product lookup
        // Group items by product_id or item_number to batch fetch
        const itemsToFetch = new Set();
        itemsToLookup.forEach(item => {
          // Fetch if purchase_price is null/undefined (not if it's 0, as 0 is a valid value)
          // Also fetch if we need product_id
          const needsPurchasePrice = item.purchase_price === null || item.purchase_price === undefined;
          const needsProductId = !item.product_id;
          
          if (needsPurchasePrice || needsProductId) {
            if (item.product_id) {
              itemsToFetch.add(`id:${item.product_id}`);
            } else if (item.item_number) {
              itemsToFetch.add(`item_number:${item.item_number}`);
            }
          }
        });

        // Batch fetch products
        const productCache = {}; // Cache products by ID or item_number
        const fetchPromises = [];
        
        // Group by type (ID vs item_number) for efficient batching
        const productIds = [];
        const itemNumbers = [];
        
        itemsToFetch.forEach(key => {
          if (key.startsWith('id:')) {
            productIds.push(key.replace('id:', ''));
          } else if (key.startsWith('item_number:')) {
            itemNumbers.push(key.replace('item_number:', ''));
          }
        });

        // Fetch products by IDs in batch
        if (productIds.length > 0) {
          fetchPromises.push(
            fetchProducts({
              ids: productIds.join(','),
              page: 1,
              per_page: productIds.length,
              fields: "id,title,displayTitle,item_number,purchase_price,sales_price,unit,unit_id",
            }).then(response => {
              if (response?.data) {
                response.data.forEach(product => {
                  productCache[`id:${product.id}`] = product;
                  if (product.item_number) {
                    productCache[`item_number:${product.item_number}`] = product;
                  }
                });
              }
            }).catch(error => {
              console.error("Error batch fetching products by ID:", error);
            })
          );
        }

        // Fetch products by item_numbers (need individual queries or batch if API supports)
        if (itemNumbers.length > 0) {
          // Fetch in smaller batches to avoid overwhelming the API
          const batchSize = 10;
          for (let i = 0; i < itemNumbers.length; i += batchSize) {
            const batch = itemNumbers.slice(i, i + batchSize);
            batch.forEach(itemNumber => {
              fetchPromises.push(
                fetchProducts({
                  query: itemNumber,
                  page: 1,
                  per_page: 10,
                  fields: "id,title,displayTitle,item_number,purchase_price,sales_price,unit,unit_id",
                }).then(response => {
                  if (response?.data && response.data.length > 0) {
                    // Find exact match by item_number
                    const product = response.data.find(p => p.item_number === itemNumber);
                    if (product) {
                      productCache[`item_number:${itemNumber}`] = product;
                      productCache[`id:${product.id}`] = product;
                    }
                  }
                }).catch(error => {
                  console.error(`Error fetching product by item_number ${itemNumber}:`, error);
                })
              );
            });
          }
        }

        // Wait for all product fetches to complete
        await Promise.all(fetchPromises);

        // Build final items with product data
        const items = itemsToLookup.map(item => {
          let product = null;
          const cacheKey = item.product_id 
            ? `id:${item.product_id}` 
            : item.item_number 
            ? `item_number:${item.item_number}` 
            : null;
          
          if (cacheKey) {
            product = productCache[cacheKey];
          }
          
          // Build final item with product data if available
          // Use purchase_price from CSV if provided (including 0), otherwise use product's purchase_price
          const finalPurchasePrice = (item.purchase_price !== null && item.purchase_price !== undefined)
            ? item.purchase_price
            : (product?.purchase_price || 0);
          
          return {
            product_id: item.product_id || (product?.id ? product.id.toString() : null),
            item_number: item.item_number || product?.item_number || null,
            title: item.title || product?.displayTitle || product?.title || null,
            quantity: item.quantity,
            purchase_price: finalPurchasePrice,
            note: item.note || "",
            row_number: item.row_number,
          };
        });

        if (items.length === 0) {
          setErrors([t("inventory.count.csv.no_valid_items")]);
          setIsProcessing(false);
          setImportProgress({ current: 0, total: 0 });
          return;
        }

        // Send all items to API in one bulk request - API will handle product lookup
        await performImport(items, importErrors);
      };
      reader.onerror = () => {
        setErrors([t("inventory.count.csv.read_error")]);
        setIsProcessing(false);
        setImportProgress({ current: 0, total: 0 });
      };
      // Try UTF-8 first, which handles most cases including Norwegian characters
      reader.readAsText(file, "UTF-8");
    } catch (error) {
      console.error("Error processing file:", error);
      setErrors([t("inventory.count.csv.processing_error")]);
      setIsProcessing(false);
      setImportProgress({ current: 0, total: 0 });
    }
  };

  const resetDialog = () => {
    setFile(null);
    setCsvData([]);
    setHeaders([]);
    setColumnMapping({});
    setPreviewData([]);
    setErrors([]);
    setMissingProducts([]);
    setPendingItems([]);
    setProductChoices({});
    setImportProgress({ current: 0, total: 0 });
  };

  const handleMissingProductsConfirm = async () => {
    setIsProcessing(true);
    setImportProgress({ current: 0, total: missingProducts.length });
    
    try {
      // Create a map of unique keys to items for easier lookup
      const missingProductsMap = {};
      missingProducts.forEach((item, index) => {
        const uniqueKey = `${item.row_number || index}_${item.item_number || index}`;
        missingProductsMap[uniqueKey] = item;
      });
      
      // First, create new products for items where user chose "add" - in bulk
      const itemsToCreate = missingProducts.filter((item, index) => {
        const uniqueKey = `${item.row_number || index}_${item.item_number || index}`;
        return productChoices[uniqueKey] === 'add';
      });
      
      const createdProducts = {}; // Map item_number to product_id
      
      if (itemsToCreate.length > 0) {
        setImportProgress({ current: 0, total: itemsToCreate.length });
        try {
          // Prepare products data for bulk create
          const productsToCreate = itemsToCreate
            .filter(item => item.item_number) // Only include items with item_number
            .map((item, idx) => {
              setImportProgress({ current: idx + 1, total: itemsToCreate.length });
              return {
                item_number: item.item_number,
                title: item.title || item.item_number, // Use title from CSV if mapped, otherwise use item_number
                active: true,
              };
            });
          
          // Create all products in bulk
          const bulkResponse = await bulkCreateProducts(productsToCreate);
          
          if (bulkResponse?.success && bulkResponse?.data) {
            // Map created products by item_number
            bulkResponse.data.forEach((product) => {
              if (product.item_number) {
                createdProducts[product.item_number] = product.id;
              }
            });
            
            if (bulkResponse.errors && bulkResponse.errors.length > 0) {
              bulkResponse.errors.forEach(error => toast.error(error));
            }
          } else {
            // Fallback to individual creation if bulk fails
            console.warn("Bulk create failed, falling back to individual creation");
            for (let idx = 0; idx < itemsToCreate.length; idx++) {
              const item = itemsToCreate[idx];
              if (!item.item_number) continue;
              
              setImportProgress({ current: idx + 1, total: itemsToCreate.length });
              
              try {
                const productData = {
                  item_number: item.item_number,
                  title: item.title || item.item_number,
                  active: true,
                };
                
                const newProduct = await createProduct(productData);
                if (newProduct?.id) {
                  createdProducts[item.item_number] = newProduct.id;
                }
              } catch (error) {
                console.error(`Error creating product ${item.item_number}:`, error);
                toast.error(`Kunne ikke opprette produkt ${item.item_number}`);
              }
            }
          }
        } catch (error) {
          console.error("Error in bulk product creation:", error);
          toast.error(t("inventory.count.csv.bulk_create_error"));
          // Fallback to individual creation
          for (let idx = 0; idx < itemsToCreate.length; idx++) {
            const item = itemsToCreate[idx];
            if (!item.item_number) continue;
            
            setImportProgress({ current: idx + 1, total: itemsToCreate.length });
            
            try {
              const productData = {
                item_number: item.item_number,
                title: item.title || item.item_number,
                active: true,
              };
              
              const newProduct = await createProduct(productData);
              if (newProduct?.id) {
                createdProducts[item.item_number] = newProduct.id;
              }
            } catch (err) {
              console.error(`Error creating product ${item.item_number}:`, err);
            }
          }
        }
      }

      // Build items to import based on user choices
      const itemsToImport = [];
      
      missingProducts.forEach((missingProduct, index) => {
        const uniqueKey = `${missingProduct.row_number || index}_${missingProduct.item_number || index}`;
        const choice = productChoices[uniqueKey];
        
        if (choice === 'add' && createdProducts[missingProduct.item_number]) {
          // User chose to add, and product was created
          itemsToImport.push({
            product_id: createdProducts[missingProduct.item_number],
            item_number: missingProduct.item_number,
            quantity: missingProduct.quantity,
            purchase_price: missingProduct.purchase_price || 0,
            note: missingProduct.note || "",
          });
        }
        // If choice is 'skip', we don't add it to itemsToImport
      });

      // Also include items that were successfully imported (from pendingItems that have product_id)
      pendingItems.forEach((item) => {
        if (item.product_id && !itemsToImport.find(i => i.product_id === item.product_id && i.item_number === item.item_number)) {
          itemsToImport.push({
            product_id: item.product_id,
            item_number: item.item_number,
            quantity: item.quantity,
            purchase_price: item.purchase_price || 0,
            note: item.note || "",
          });
        }
      });

      // Close validation dialog first
      setMissingProducts([]);
      setPendingItems([]);
      setProductChoices({});
      
      if (itemsToImport.length === 0) {
        toast.error(t("inventory.count.csv.no_items_to_import"));
        setIsProcessing(false);
        setImportProgress({ current: 0, total: 0 });
        return;
      }
      
      // Proceed with import (performImport will close main dialog on success)
      await performImport(itemsToImport);
    } catch (error) {
      console.error("Error handling missing products:", error);
      toast.error(t("inventory.count.csv.import_error"));
      setIsProcessing(false);
      setImportProgress({ current: 0, total: 0 });
    }
  };

  return (
    <>
    <Transition.Root show={open}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-4xl sm:p-6">
                <div className="mb-4 flex items-center justify-between">
                  <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">
                    {t("inventory.count.csv.import_title")}
                  </Dialog.Title>
                  <button
                    onClick={() => {
                      resetDialog();
                      onClose();
                    }}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                {errors.length > 0 && (
                  <div className="mb-4">
                    <Alert severity="warning">
                      <div className="max-h-40 overflow-y-auto">
                        {errors.map((error, index) => (
                          <div key={index}>{error}</div>
                        ))}
                      </div>
                    </Alert>
                  </div>
                )}

                {!file ? (
                  <div
                    {...getRootProps()}
                    className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                      isDragActive
                        ? "border-blue-400 bg-blue-50"
                        : "border-gray-300 hover:border-gray-400"
                    }`}
                  >
                    <input {...getInputProps()} ref={fileInputRef} />
                    <DocumentArrowUpIcon className="mx-auto h-12 w-12 text-gray-400" />
                    <p className="mt-2 text-sm text-gray-600">
                      {isDragActive
                        ? t("inventory.count.csv.drop_here")
                        : t("inventory.count.csv.drop_or_select")}
                    </p>
                    <p className="mt-1 text-xs text-gray-500">
                      {t("inventory.count.csv.supported_formats")}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-900">{file.name}</p>
                        <p className="text-xs text-gray-500">
                          {t("inventory.count.csv.file_selected")}
                        </p>
                      </div>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => {
                          resetDialog();
                          fileInputRef.current?.click();
                        }}
                      >
                        {t("inventory.count.csv.change_file")}
                      </Button>
                    </div>

                    {headers.length > 0 && (
                      <>
                        <div>
                          <h4 className="text-sm font-medium text-gray-900 mb-2">
                            {t("inventory.count.csv.column_mapping")}
                          </h4>
                          <div className="grid grid-cols-2 gap-4">
                            {Object.keys(fieldMappings).map((field) => (
                              <FormControl key={field} size="small" fullWidth>
                                <InputLabel>
                                  {t(`inventory.count.csv.field.${field}`)}
                                </InputLabel>
                                <Select
                                  value={columnMapping[field] || ""}
                                  label={t(`inventory.count.csv.field.${field}`)}
                                  onChange={(e) => {
                                    setColumnMapping({
                                      ...columnMapping,
                                      [field]: e.target.value,
                                    });
                                  }}
                                >
                                  <MenuItem value="">
                                    <em>{t("inventory.count.csv.not_mapped")}</em>
                                  </MenuItem>
                                  {headers.map((header) => (
                                    <MenuItem key={header} value={header}>
                                      {normalizeText(header)}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            ))}
                          </div>
                        </div>

                        {previewData.length > 0 && (
                          <div>
                            <h4 className="text-sm font-medium text-gray-900 mb-2">
                              {t("inventory.count.csv.preview")} (10 {t("inventory.count.csv.first_rows")})
                            </h4>
                            <div className="max-h-60 overflow-auto border rounded">
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    {headers.map((header) => (
                                      <TableCell key={header}>{normalizeText(header)}</TableCell>
                                    ))}
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {previewData.map((row, index) => (
                                    <TableRow key={index}>
                                      {headers.map((header) => (
                                        <TableCell key={header}>{normalizeText(row[header] || "-")}</TableCell>
                                      ))}
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </div>
                          </div>
                        )}

                        {isProcessing && importProgress.total > 0 && (
                          <div className="mb-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-sm font-medium text-blue-900">
                                {t("inventory.count.csv.processing_items", { 
                                  current: importProgress.current, 
                                  total: importProgress.total 
                                })}
                              </span>
                              <span className="text-sm text-blue-700">
                                {Math.round((importProgress.current / importProgress.total) * 100)}%
                              </span>
                            </div>
                            <div className="w-full bg-blue-200 rounded-full h-2">
                              <div 
                                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                                style={{ width: `${(importProgress.current / importProgress.total) * 100}%` }}
                              />
                            </div>
                          </div>
                        )}
                        <div className="flex justify-end space-x-2 pt-4">
                          <Button
                            variant="outlined"
                            onClick={() => {
                              resetDialog();
                              onClose();
                            }}
                            disabled={isProcessing}
                          >
                            {t("Cancel")}
                          </Button>
                          <Button
                            variant="contained"
                            onClick={handleImport}
                            disabled={isProcessing || (!columnMapping.product_id && !columnMapping.item_number)}
                          >
                            {isProcessing
                              ? t("inventory.count.csv.importing")
                              : t("inventory.count.csv.import")}
                          </Button>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>

      {/* Missing Products Validation Dialog */}
      <Transition.Root show={missingProducts.length > 0}>
        <Dialog as="div" className="relative z-50" onClose={() => {}}>
          <Transition.Child
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-2xl sm:p-6">
                  <div className="mb-4 flex items-center justify-between">
                    <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">
                      {t("inventory.count.csv.missing_products_title")}
                    </Dialog.Title>
                    <button
                      onClick={() => {
                        setMissingProducts([]);
                        setPendingItems([]);
                        setProductChoices({});
                      }}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <XMarkIcon className="h-6 w-6" />
                    </button>
                  </div>

                  <div className="mb-4">
                    <p className="text-sm text-gray-600 mb-4">
                      {t("inventory.count.csv.missing_products_message", { count: missingProducts.length })}
                    </p>
                    
                    <div className="max-h-96 overflow-y-auto border rounded">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>{t("inventory.count.csv.field.item_number")}</TableCell>
                            <TableCell>{t("inventory.count.csv.field.title")}</TableCell>
                            <TableCell>{t("inventory.count.csv.field.quantity")}</TableCell>
                            <TableCell align="center">{t("inventory.count.csv.action")}</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {missingProducts.map((item, index) => {
                            // Use a unique key combining row number and item_number
                            const uniqueKey = `${item.row_number || index}_${item.item_number || index}`;
                            const choiceKey = uniqueKey; // Use unique key for productChoices
                            
                            return (
                              <TableRow key={uniqueKey}>
                                <TableCell>
                                  <span className="font-medium">{item.item_number || `Row ${item.row_number || index + 1}`}</span>
                                </TableCell>
                                <TableCell>
                                  {item.title ? (
                                    <span className="text-gray-900">{item.title}</span>
                                  ) : (
                                    <span className="text-gray-400 italic">-</span>
                                  )}
                                  {item.note && (
                                    <div className="text-xs text-gray-500 mt-1">{item.note}</div>
                                  )}
                                </TableCell>
                                <TableCell>{item.quantity || 0}</TableCell>
                                <TableCell align="center">
                                  <div className="flex gap-2 justify-center">
                                    <Button
                                      size="small"
                                      variant={productChoices[choiceKey] === 'add' ? 'contained' : 'outlined'}
                                      color="primary"
                                      onClick={() => {
                                        setProductChoices({
                                          ...productChoices,
                                          [choiceKey]: 'add',
                                        });
                                      }}
                                    >
                                      {t("inventory.count.csv.add_as_new")}
                                    </Button>
                                    <Button
                                      size="small"
                                      variant={productChoices[choiceKey] === 'skip' ? 'contained' : 'outlined'}
                                      color="secondary"
                                      onClick={() => {
                                        setProductChoices({
                                          ...productChoices,
                                          [choiceKey]: 'skip',
                                        });
                                      }}
                                    >
                                      {t("inventory.count.csv.skip")}
                                    </Button>
                                  </div>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>
                  </div>

                  <div className="flex justify-end space-x-2 pt-4 border-t">
                    <Button
                      variant="outlined"
                      onClick={() => {
                        setMissingProducts([]);
                        setPendingItems([]);
                        setProductChoices({});
                      }}
                    >
                      {t("Cancel")}
                    </Button>
                    {isProcessing && importProgress.total > 0 && (
                      <div className="mb-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-blue-900">
                            {t("inventory.count.csv.processing_items", { 
                              current: importProgress.current, 
                              total: importProgress.total 
                            })}
                          </span>
                          <span className="text-sm text-blue-700">
                            {Math.round((importProgress.current / importProgress.total) * 100)}%
                          </span>
                        </div>
                        <div className="w-full bg-blue-200 rounded-full h-2">
                          <div 
                            className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${(importProgress.current / importProgress.total) * 100}%` }}
                          />
                        </div>
                      </div>
                    )}
                    <Button
                      variant="contained"
                      onClick={handleMissingProductsConfirm}
                      disabled={
                        isProcessing ||
                        missingProducts.some((item, index) => {
                          const uniqueKey = `${item.row_number || index}_${item.item_number || index}`;
                          return !productChoices[uniqueKey];
                        })
                      }
                    >
                      {isProcessing
                        ? t("inventory.count.csv.processing")
                        : t("inventory.count.csv.continue_import")}
                    </Button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </>
  );
};



